import type React from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react'

interface ForceLogoutContextValue {
  forceLogout: () => void
  registerLogout: (fn: () => void) => void
}

const ForceLogoutContext = createContext<ForceLogoutContextValue>({
  forceLogout: () => {},
  registerLogout: () => {},
})

export const useForceLogout = () => useContext(ForceLogoutContext).forceLogout
export const useRegisterLogout = () =>
  useContext(ForceLogoutContext).registerLogout

/**
 * Rendered inside SessionProvider to register the actual logout function
 * into the ForceLogoutProvider's ref so `forceLogout()` works.
 */
export const ForceLogoutHandler = ({ logout }: { logout: () => void }) => {
  const registerLogout = useContext(ForceLogoutContext).registerLogout
  useEffect(() => {
    registerLogout(logout)
  }, [logout, registerLogout])
  return null
}

const ForceLogoutProvider = ({ children }: React.PropsWithChildren) => {
  const logoutRef = useRef<(() => void) | null>(null)

  const forceLogout = useCallback(() => {
    logoutRef.current?.()
  }, [])

  const registerLogout = useCallback((fn: () => void) => {
    logoutRef.current = fn
  }, [])

  const value = { forceLogout, registerLogout }

  return (
    <ForceLogoutContext.Provider value={value}>
      {children}
    </ForceLogoutContext.Provider>
  )
}

export const ForceLogout = {
  Provider: ForceLogoutProvider,
  Handler: ForceLogoutHandler,
}
